/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  SEAT LAYOUT (ROOM) MODEL
 * ─────────────────────────────────────────────────────────────────────────────
 * A room is an ordered list of ROWS; a row is an ordered list of SLOTS. Every
 * slot is one of three states:
 *
 *   bookable  a bike/bed exists and can be booked
 *   oos       a bike exists but is out of service (temporal, not structural)
 *   gap       nothing exists here — a walkway (structural)
 *
 * Rows have independent lengths and are centre-aligned when rendered, which is
 * what produces the theatre curve.
 *
 * ── WHY IDENTITY IS SEPARATE FROM THE NUMBER ────────────────────────────────
 * `seatId` is immutable and generated once. It is what a booking, a favourite,
 * and a maintenance record will point at. `label` is the number painted on the
 * physical bike — it is SEEDED from position when the slot is created and then
 * never renumbered automatically.
 *
 * That distinction is the whole ballgame. If the display number were a computed
 * index, inserting one walkway would renumber every bike after it: seat 14
 * becomes seat 15, and every existing booking silently points at a different
 * machine while the decal on the bike still says 14. Renumbering is therefore an
 * explicit action (`renumberAll`) the studio takes deliberately, never a side
 * effect of an edit.
 *
 * This file is deliberately shaped like the documents the backend will store, so
 * moving from the local store to a real API is a swap of the service layer only.
 */

export type SlotState = 'bookable' | 'oos' | 'gap';

/** The click-cycle order in the editor: bookable → out of service → gap → … */
export const SEAT_CYCLE: SlotState[] = ['bookable', 'oos', 'gap'];

export interface SeatPosition {
  /** Immutable identity. Bookings/favourites/maintenance key on this, never on `label`. */
  seatId: string;
  state: SlotState;
  /**
   * The decal number. Null on a gap that never held a bike.
   *
   * A slot CYCLED to `gap` deliberately keeps its old decal so cycling back is
   * lossless — losing the number on a mis-click would mean walking to the bike
   * to read it again. Consumers must therefore gate on `state`, not on whether
   * `label` is present: `renumberAll` and every count helper skip gaps regardless.
   */
  label: string | null;
  /** Per-slot attribute rather than a fourth cycle state — cycling 4 states is worse UX. */
  isAccessible?: boolean;
  /** Only meaningful while state === 'oos'. */
  oosReason?: string | null;
  oosExpectedBackAt?: string | null;
}

export interface SeatRow {
  rowId: string;
  /** 'A', 'B', … — display only; identity is `rowId`. */
  label: string;
  /**
   * Extra charge in ₹ for every bookable slot in this row. 0 = none.
   *
   * Stored per-row rather than per-room so "front row costs more" generalises
   * beyond row A at no extra cost. STORED BUT NOT CHARGED until the backend
   * phase that answers what a pack booking pays for a surcharged seat — there is
   * no partial-payment booking path today.
   */
  surcharge: number;
  seats: SeatPosition[];
}

/**
 * Which wall the instructor is against. Handles mirrored rooms for free.
 * A genuinely L-shaped floor cannot be expressed as rows-of-seats — that would
 * need free (x,y) placement, i.e. a different editor. The stable `seatId` model
 * survives that change, so it stays a viable v2 without a booking migration.
 */
export type RoomOrientation =
  | 'instructor_top'
  | 'instructor_bottom'
  | 'instructor_left'
  | 'instructor_right';

export interface SeatLayout {
  _id: string;
  /** Rooms are per-studio. */
  studio: string;
  studioName?: string;
  name: string;
  description?: string;
  rows: SeatRow[];
  /**
   * How bike numbers are assigned.
   *
   *   true  (default) — AUTO. Numbers are positional and recomputed on every
   *                     structural edit: 1…N top-left to bottom-right, skipping
   *                     walkways. Remove a bike and everything after it shifts
   *                     down, so the room always reads 1…N with no holes.
   *   false           — MANUAL. Numbers are whatever the studio typed and are
   *                     never touched by an edit. This is the mode a real room
   *                     with physical decals needs, because a decal does not
   *                     move when you insert a walkway.
   *
   * Editing any single bike's number switches the room to MANUAL — otherwise the
   * next edit would silently overwrite what was just typed. "Renumber bikes"
   * switches back to AUTO.
   */
  autoNumber: boolean;
  orientation: RoomOrientation;
  /** Anchor captions drawn at either end of the room. */
  frontLabel: string;
  backLabel: string;
  /**
   * Server-derived, read-only here. `bookableCount` is what a session's capacity
   * is set from; `version` is the room's edition, which a client compares against
   * its cached copy. Never sent on write — the service owns both.
   */
  bookableCount: number;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/* ── Limits ────────────────────────────────────────────────────────────────────
 * One place, so they change without hunting through components.
 *
 * MAX_SLOTS_PER_ROW is 24, not 16: gaps consume seats, so a row showing 5 bikes
 * with a walkway between each already burns 10 seats. 16 runs out fast on a
 * sparse row.
 */
export const LAYOUT_LIMITS = {
  MAX_ROWS: 12,
  MAX_SLOTS_PER_ROW: 24,
  MIN_SLOTS_PER_ROW: 1,
  MAX_SURCHARGE: 5000,
} as const;

/* ── Derived values ───────────────────────────────────────────────────────── */

export const isPhysicalSeat = (s: SeatPosition): boolean => s.state !== 'gap';

/** Bookable seats — this is what class capacity will be derived from. */
export const bookableCount = (layout: Pick<SeatLayout, 'rows'>): number =>
  layout.rows.reduce(
    (n, r) => n + r.seats.filter((s) => s.state === 'bookable').length,
    0,
  );

export const oosCount = (layout: Pick<SeatLayout, 'rows'>): number =>
  layout.rows.reduce(
    (n, r) => n + r.seats.filter((s) => s.state === 'oos').length,
    0,
  );

/** Every bike that physically exists, bookable or not. */
export const physicalCount = (layout: Pick<SeatLayout, 'rows'>): number =>
  layout.rows.reduce((n, r) => n + r.seats.filter(isPhysicalSeat).length, 0);

export const slotCount = (layout: Pick<SeatLayout, 'rows'>): number =>
  layout.rows.reduce((n, r) => n + r.seats.length, 0);

/** Widest row, used to size the canvas. */
export const maxRowLength = (layout: Pick<SeatLayout, 'rows'>): number =>
  layout.rows.reduce((n, r) => Math.max(n, r.seats.length), 0);

/** Rows that carry a surcharge, for the legend. */
export const surchargedRows = (layout: Pick<SeatLayout, 'rows'>): SeatRow[] =>
  layout.rows.filter((r) => r.surcharge > 0);

/* ── Construction ─────────────────────────────────────────────────────────── */

let idCounter = 0;
/**
 * Client-side id. Deliberately prefixed and time+counter based rather than a
 * plain index so ids stay unique within a session and are obviously not
 * ObjectIds — the backend will mint real ones and these become the seed values
 * of a one-time import.
 */
export const newId = (prefix: string): string =>
  `${prefix}_${Date.now().toString(36)}${(idCounter++).toString(36)}`;

/** A, B, … Z, AA, AB — spreadsheet-style so row 27 is still sensible. */
export const rowLabelForIndex = (index: number): string => {
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
};

/**
 * The next decal number to hand out: one past the highest numeric label already
 * in the room. Uses the MAX rather than a count so a new slot never collides
 * with an existing decal after rows have been deleted.
 */
export const nextLabelNumber = (layout: Pick<SeatLayout, 'rows'>): number => {
  let max = 0;
  for (const r of layout.rows) {
    for (const s of r.seats) {
      const n = Number(s.label);
      if (Number.isFinite(n) && n > max) max = n;
    }
  }
  return max + 1;
};

export const makeSeat = (state: SlotState, label: string | null): SeatPosition => ({
  seatId: newId('slot'),
  state,
  label: state === 'gap' ? null : label,
});

/**
 * Assign sequential decals in positional order (top-left → bottom-right),
 * skipping gaps. Out-of-service bikes DO get a number: the machine physically
 * exists and carries a decal, it just cannot be booked today.
 *
 * Only ever called from the explicit "Renumber" action.
 */
export const renumberAll = (rows: SeatRow[]): SeatRow[] => {
  let n = 1;
  return rows.map((row) => ({
    ...row,
    seats: row.seats.map((slot) =>
      slot.state === 'gap'
        ? { ...slot, label: null }
        : { ...slot, label: String(n++) },
    ),
  }));
};

/** A sensible starting room: 4 rows of 8 bikes, numbered 1–32. */
export const makeDefaultRows = (rowCount = 4, perRow = 8): SeatRow[] => {
  let n = 1;
  return Array.from({ length: rowCount }, (_, r) => ({
    rowId: newId('row'),
    label: rowLabelForIndex(r),
    surcharge: 0,
    seats: Array.from({ length: perRow }, () =>
      makeSeat('bookable', String(n++)),
    ),
  }));
};

export const makeEmptyLayout = (
  studio: string,
  studioName?: string,
): Omit<SeatLayout, '_id' | 'createdAt' | 'updatedAt' | 'bookableCount' | 'version'> => ({
  studio,
  studioName,
  name: '',
  description: '',
  rows: makeDefaultRows(),
  autoNumber: true,
  orientation: 'instructor_top',
  frontLabel: 'INSTRUCTOR',
  backLabel: 'ENTRANCE',
});

/* ── Validation ───────────────────────────────────────────────────────────── */

export interface LayoutIssue {
  field: 'name' | 'rows' | 'labels' | 'surcharge';
  message: string;
}

/**
 * Everything that must hold before a room can be saved. Duplicate decals are an
 * error rather than a warning: two bikes labelled "12" makes the front desk's
 * job impossible and would make a future booking ambiguous to a human even
 * though `seatId` keeps it unambiguous to the database.
 */
export const validateLayout = (
  layout: Pick<SeatLayout, 'name' | 'rows'>,
): LayoutIssue[] => {
  const issues: LayoutIssue[] = [];

  if (!layout.name.trim()) {
    issues.push({ field: 'name', message: 'Give the room a name.' });
  }
  if (layout.rows.length === 0) {
    issues.push({ field: 'rows', message: 'A room needs at least one row.' });
  }
  if (bookableCount(layout) === 0) {
    issues.push({
      field: 'rows',
      message: 'A room needs at least one bookable bike.',
    });
  }

  // Plain object rather than a Map: the admin panel's build target is ES5, where
  // spreading a Map iterator needs --downlevelIteration.
  const seen: Record<string, number> = {};
  for (const row of layout.rows) {
    for (const slot of row.seats) {
      if (slot.state === 'gap' || !slot.label) continue;
      seen[slot.label] = (seen[slot.label] ?? 0) + 1;
    }
  }
  const dupes = Object.keys(seen).filter((l) => seen[l] > 1);
  if (dupes.length) {
    issues.push({
      field: 'labels',
      message: `Duplicate bike number${dupes.length > 1 ? 's' : ''}: ${dupes.join(', ')}. Use Renumber, or edit them individually.`,
    });
  }

  for (const row of layout.rows) {
    if (row.surcharge < 0 || row.surcharge > LAYOUT_LIMITS.MAX_SURCHARGE) {
      issues.push({
        field: 'surcharge',
        message: `Row ${row.label} surcharge must be between ₹0 and ₹${LAYOUT_LIMITS.MAX_SURCHARGE}.`,
      });
    }
  }

  return issues;
};
